// 1.5.15: Unified BFF API client
// All AI/Places/Share requests route through the BFF when VITE_STREAM_PROVIDER=langgraph

import { logger } from '../utils/logger';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PROJECT_REF = import.meta.env.VITE_SUPABASE_PROJECT_REF;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

const useBff = () =>
  (import.meta.env.VITE_STREAM_PROVIDER ?? 'legacy').toLowerCase() === 'langgraph';

const getBffBaseUrl = (): string => {
  const url = import.meta.env.VITE_BFF_URL;
  if (!url) throw new Error('VITE_BFF_URL is required when VITE_STREAM_PROVIDER=langgraph');
  return url.replace(/\/+$/, '');
};

/** Resolve the legacy (direct Edge Function) base URL for a given function name. */
const getLegacyBaseUrl = (functionName: string): string => {
  if (SUPABASE_URL) {
    return `${SUPABASE_URL.replace(/\/+$/, '')}/functions/v1/${functionName}`;
  }
  if (SUPABASE_PROJECT_REF) {
    return `https://${SUPABASE_PROJECT_REF}.supabase.co/functions/v1/${functionName}`;
  }
  throw new Error(
    'Missing VITE_SUPABASE_URL or VITE_SUPABASE_PROJECT_REF. Please set one in your environment variables.',
  );
};

export type ServiceName = 'ai-service' | 'maps-proxy' | 'share-service';

interface RoutingMap {
  bffPrefix: string;
  edgeFunctionName: string;
  edgePathPrefix: string;
}

const SERVICE_ROUTING: Record<ServiceName, RoutingMap> = {
  'ai-service': {
    bffPrefix: '/api/ai',
    edgeFunctionName: 'ai-service',
    edgePathPrefix: '/api/ai',
  },
  'maps-proxy': {
    bffPrefix: '/api/places',
    edgeFunctionName: 'maps-proxy',
    edgePathPrefix: '/api/maps',
  },
  'share-service': {
    bffPrefix: '/api/share',
    edgeFunctionName: 'share-service',
    edgePathPrefix: '/api/share',
  },
};

/**
 * Build the full URL for a service endpoint.
 * When BFF is active, routes through BFF; otherwise directly to Edge Function.
 *
 * @param service  Which service to target
 * @param path     Path after the service prefix (e.g. "/text-search", "/create")
 *                 For BFF: bffPrefix + path
 *                 For legacy: edgeFunction + edgePathPrefix + path
 */
export function resolveUrl(service: ServiceName, path: string): string {
  const route = SERVICE_ROUTING[service];
  if (useBff()) {
    return `${getBffBaseUrl()}${route.bffPrefix}${path}`;
  }
  return `${getLegacyBaseUrl(route.edgeFunctionName)}${route.edgePathPrefix}${path}`;
}

/**
 * Build the default headers for a request.
 * Includes Authorization with the anon key (or session token if provided).
 */
export function buildHeaders(options?: {
  contentType?: string;
  authToken?: string;
}): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = options?.authToken || SUPABASE_ANON_KEY;
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  headers['Content-Type'] = options?.contentType || 'application/json';
  return headers;
}

/**
 * Make a JSON POST request and return parsed response.
 */
export async function postJson<T>(
  service: ServiceName,
  path: string,
  body: unknown,
  options?: { authToken?: string; timeout?: number },
): Promise<T> {
  const url = resolveUrl(service, path);
  const headers = buildHeaders({ authToken: options?.authToken });

  const controller = new AbortController();
  const timeoutId = options?.timeout
    ? setTimeout(() => controller.abort(), options.timeout)
    : undefined;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    if (!response.ok) {
      let errorBody = '';
      try {
        errorBody = await response.text();
      } catch {
        // ignore
      }
      logger.error('BFF request failed:', { url, status: response.status, errorBody });
      throw new Error(`Request failed: ${response.status} ${response.statusText}`);
    }

    return (await response.json()) as T;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

/**
 * Make a GET request and return parsed JSON response.
 */
export async function getJson<T>(
  service: ServiceName,
  path: string,
  options?: { authToken?: string; params?: Record<string, string> },
): Promise<T> {
  let url = resolveUrl(service, path);
  if (options?.params) {
    const qs = new URLSearchParams(options.params).toString();
    url += `?${qs}`;
  }
  const headers = buildHeaders({ authToken: options?.authToken });

  const response = await fetch(url, { method: 'GET', headers });

  if (!response.ok) {
    let errorBody = '';
    try {
      errorBody = await response.text();
    } catch {
      // ignore
    }
    logger.error('BFF GET request failed:', { url, status: response.status, errorBody });
    throw new Error(`Request failed: ${response.status} ${response.statusText}`);
  }

  return (await response.json()) as T;
}

/**
 * Build a photo URL that can be used in <img> src.
 * For BFF mode, routes through BFF; for legacy, direct Edge Function URL.
 */
export function buildPhotoUrl(params: Record<string, string>): string {
  const qs = new URLSearchParams(params).toString();
  const route = SERVICE_ROUTING['maps-proxy'];
  if (useBff()) {
    return `${getBffBaseUrl()}${route.bffPrefix}/photo?${qs}`;
  }
  return `${getLegacyBaseUrl(route.edgeFunctionName)}${route.edgePathPrefix}/photo?${qs}`;
}

/**
 * Get the stream URL for chat (used by aiService).
 */
export function resolveStreamUrl(): string {
  if (useBff()) {
    return `${getBffBaseUrl()}/api/chat/stream`;
  }
  return `${getLegacyBaseUrl('ai-service')}/api/ai/chat-stream`;
}
